import React from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import {
  HomeOutlined,
  LocationCityOutlined,
  ArticleOutlined,
  NewspaperOutlined,
  GavelOutlined,
  AccountBalanceOutlined,
  InfoOutlined,
  PhoneOutlined,
  LockOutlined,
  AccessibilityNewOutlined,
  OpenInNew,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ListPageLayout from '../components/shared/list-page-layout';
import { IMenuResponse } from '../../model/base/response/menu-response';
import { IMenuItemResponse } from '../../model/base/response/menu-item-reponse';

interface SitemapViewProps {
  entityName?: string;
  menus: IMenuResponse[];
}

interface SitemapItem {
  label: string;
  path?: string;
  external?: boolean;
  children?: SitemapItem[];
}

interface SitemapSection {
  title: string;
  icon: React.ReactNode;
  items: SitemapItem[];
}

export const SitemapView: React.FC<SitemapViewProps> = ({ menus }) => {
  const navigate = useNavigate();
  const routeParams = useParams<{ state?: string; city?: string }>();

  const buildPath = (path: string): string => {
    if (!routeParams.state || !routeParams.city) return path;
    if (path === '/') return `/${routeParams.state}/${routeParams.city}`;
    return `/${routeParams.state}/${routeParams.city}${path}`;
  };

  const handleNavigate = (path: string, external?: boolean) => {
    const isExternal =
      external ||
      path.startsWith('http://') ||
      path.startsWith('https://') ||
      path.startsWith('//');
    if (isExternal) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      const absolutePath = path.startsWith('/') ? path : `/${path}`;
      navigate(buildPath(absolutePath));
    }
  };

  const staticSections: SitemapSection[] = [
    {
      title: 'Início',
      icon: <HomeOutlined sx={{ fontSize: 28 }} />,
      items: [
        { label: 'Página Inicial', path: '/' },
      ],
    },
    {
      title: 'O Município',
      icon: <LocationCityOutlined sx={{ fontSize: 28 }} />,
      items: [
        { label: 'A Cidade', path: '/a-cidade' },
        { label: 'Estrutura Administrativa', path: '/estrutura-administrativa' },
        { label: 'Projetos Governamentais', path: '/projetos-governamentais' },
      ],
    },
    {
      title: 'Notícias',
      icon: <NewspaperOutlined sx={{ fontSize: 28 }} />,
      items: [
        { label: 'Todas as Notícias', path: '/noticias' },
      ],
    },
    {
      title: 'Diário Oficial',
      icon: <ArticleOutlined sx={{ fontSize: 28 }} />,
      items: [
        { label: 'Edições', path: '/diario-oficial' },
        { label: 'Publicações', path: '/publicacoes' },
      ],
    },
    {
      title: 'Serviços',
      icon: <InfoOutlined sx={{ fontSize: 28 }} />,
      items: [
        { label: 'Perguntas Frequentes', path: '/perguntas-respostas' },
        { label: 'Links Úteis', path: '/links-uteis' },
      ],
    },
    {
      title: 'Transparência',
      icon: <AccountBalanceOutlined sx={{ fontSize: 28 }} />,
      items: [
        { label: 'Contas Públicas', path: '/contas-publicas' },
        { label: 'Licitações', path: '/licitacoes' },
        {
          label: 'Gestão Fiscal',
          children: [
            { label: 'Lei de Diretrizes Orçamentárias (LDO)', path: '/publicacoes/4/40' },
            { label: 'Plano Plurianual (PPA)', path: '/publicacoes/4/43' },
            { label: 'Lei Orçamentária Anual (LOA)', path: '/publicacoes/4/41' },
            { label: 'Relatório de Gestão Fiscal (RGF)', path: '/publicacoes/4/45' },
            { label: 'Relatório Resumido da Execução Orçamentária (RREO)', path: '/publicacoes/4/46' },
          ],
        },
        { label: 'Contratos e Aquisições', path: '/contratos-aquisicoes' },
        { label: 'Convênios e Repasses', path: '/convenios-repasses' },
        { label: 'Auditoria', path: '/auditoria' },
      ],
    },
    {
      title: 'Fale Conosco',
      icon: <PhoneOutlined sx={{ fontSize: 28 }} />,
      items: [
        { label: 'Formulário de Contato', path: '/fale-conosco' },
      ],
    },
    {
      title: 'Informações Adicionais',
      icon: <LockOutlined sx={{ fontSize: 28 }} />,
      items: [
        { label: 'Acessibilidade', path: '/acessibilidade' },
        { label: 'Termos de Uso', path: '/termos-de-uso' },
        { label: 'Política de Privacidade', path: '/politica-de-privacidade' },
      ],
    },
  ];

  const getMegaMenuSections = (): SitemapSection[] => {
    if (!menus || menus.length === 0) return [];

    return menus
      .filter((menu) => menu.items && menu.items.some((item) => item.isPublished !== false))
      .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0))
      .map((menu) => {
        const publishedItems: IMenuItemResponse[] = (menu.items || [])
          .filter((item) => item.isPublished !== false)
          .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));

        return {
          title: menu.name || '',
          icon: <GavelOutlined sx={{ fontSize: 28 }} />,
          items: publishedItems.map((item) => ({
            label: item.name || '',
            path: item.url || '',
            external: item.isExternal,
          })),
        };
      });
  };

  const megaMenuSections = getMegaMenuSections();
  const allSections = [...staticSections, ...megaMenuSections];

  const isExternalUrl = (path?: string, external?: boolean): boolean => {
    if (external) return true;
    if (!path) return false;
    return path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//');
  };

  const renderItem = (item: SitemapItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const external = isExternalUrl(item.path, item.external);

    return (
      <Box key={item.label}>
        {hasChildren ? (
          <Box sx={{ mt: depth === 0 ? 0.5 : 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                py: 0.5,
                pl: depth > 0 ? 2 : 0,
              }}
            >
              <ChevronRight sx={{ fontSize: 16, color: 'primary.main' }} />
              {item.label}
            </Typography>
            <Box sx={{ pl: 2 }}>
              {item.children!.map((child) => renderItem(child, depth + 1))}
            </Box>
          </Box>
        ) : (
          <Box
            onClick={() => item.path && handleNavigate(item.path, external)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              py: 0.5,
              pl: depth > 0 ? 2 : 0,
              cursor: item.path ? 'pointer' : 'default',
              borderRadius: 1,
              '&:hover': item.path
                ? {
                    backgroundColor: 'action.hover',
                    '& .link-text': { color: 'primary.main', textDecoration: 'underline' },
                  }
                : {},
              transition: 'background-color 0.15s ease',
            }}
          >
            <ChevronRight sx={{ fontSize: 16, color: 'primary.light', flexShrink: 0 }} />
            <Typography
              variant="body2"
              className="link-text"
              sx={{
                color: item.path ? 'text.secondary' : 'text.disabled',
                transition: 'color 0.15s ease',
                flex: 1,
              }}
            >
              {item.label}
            </Typography>
            {external && (
              <OpenInNew sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <ListPageLayout
      title="Mapa do Site"
      subtitle="Navegue por todas as seções e páginas disponíveis neste portal"
      breadcrumbs={[{ label: 'Mapa do Site' }]}
    >
      {/* Cabeçalho informativo */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AccessibilityNewOutlined sx={{ fontSize: 32 }} />
            <Typography variant="h5" component="h2">
              Mapa do Site
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Esta página lista todas as seções e links disponíveis no portal municipal,
            facilitando a navegação e o acesso às informações de forma organizada.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`${allSections.length} seções`}
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            {megaMenuSections.length > 0 && (
              <Chip
                label={`${megaMenuSections.length} categorias de Acesso a Informações`}
                size="small"
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Seções estáticas */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
        Navegação Principal
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {staticSections.map((section) => (
          <Grid item xs={12} sm={6} md={4} key={section.title}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ p: 3, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: 'white',
                      flexShrink: 0,
                    }}
                  >
                    {section.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {section.title}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                <Box>
                  {section.items.map((item) => renderItem(item))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Seções do Mega Menu (Acesso a Informações) */}
      {megaMenuSections.length > 0 && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
            Acesso a Informações
          </Typography>

          <Grid container spacing={3}>
            {megaMenuSections.map((section) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={section.title}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          backgroundColor: 'primary.light',
                          color: 'white',
                          flexShrink: 0,
                        }}
                      >
                        <GavelOutlined sx={{ fontSize: 22 }} />
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.3 }}
                      >
                        {section.title}
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 1.5 }} />

                    <Box>
                      {section.items.map((item) => renderItem(item))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </ListPageLayout>
  );
};
